/**
 * La **cobertura de una corrida como cascada**: cuántos pedidos se cargaron, qué se
 * descuenta en cada tramo y cuántos quedaron en ruta.
 *
 * Por qué existe: la sección de cobertura mostraba una fila plana de cantidades cuyo
 * titular era `plannable` —"283 de 289 pedidos se optimizaron"—, y `plannable` no es un
 * resultado: se calcula **antes** del optimizador y solo significa que el pedido tiene
 * coordenadas e identidad. Entre ese número y la realidad se pierden los que no tienen
 * flota y los que el optimizador no pudo ubicar. Con unos descuentos ya restados del
 * titular y otros no, la suma no cuadra y la conclusión razonable es que se perdieron
 * pedidos.
 *
 * La cascada hace visible de qué total sale cada resta, y el titular pasa a ser el
 * resultado. La invariante es una sola: **descuentos + en ruta = cargados**.
 *
 * Módulo puro: entran las cantidades, salen los niveles y el titular. Sin HTTP, sin
 * Angular.
 */

/** Qué lista abre un descuento para poder revisar sus pedidos. */
export type DropTarget = 'excluded' | 'fleet-gap' | 'unassigned';

/** Una cantidad que no entra, con la lista donde se revisa. */
export interface FunnelDrop {
  count: number;
  label: string;
  target: DropTarget;
  /** Qué hacer al respecto, cuando hay algo concreto que decir. */
  hint?: string;
}

/** Un tramo de la cascada: cuántos quedan y qué se les resta. */
export interface FunnelLevel {
  count: number;
  label: string;
  /**
   * Otra MAGNITUD del mismo nivel, cuando la unidad cambia acá.
   *
   * Es el único lugar donde se nombran las visitas: como magnitud paralela y no como
   * denominador. Dos unidades dentro de la misma resta no cierran por construcción.
   */
  aside?: string;
  drops: FunnelDrop[];
}

export interface CoverageFunnelInput {
  loaded: number;
  invalidGeo: number;
  missingIdentity: number;
  /** Con coordenadas e identidad. NO es "optimizados". */
  plannable: number;
  /** Planificables sin flota que los pueda llevar. */
  unplanned: number;
  /**
   * Pedidos que SÍ llegaron al optimizador (`coverage.planned`).
   *
   * Es la autoridad cuando viene: la partición ya lo cuenta como
   * `Σ group.orders.length`, y recalcularlo acá sería una segunda definición del
   * mismo concepto. Ausente, se deriva de `plannable − unplanned`, que es la
   * invariante que la partición ya sostiene.
   */
  reached?: number;
  /** Pedidos de las visitas que el optimizador no ubicó. */
  unassignedOrders: number;
  /** Visitas que llegaron al optimizador, para declarar la equivalencia. */
  reachedVisits: number;
}

export interface CoverageFunnel {
  loaded: number;
  levels: FunnelLevel[];
  /** Pedidos que quedaron en ruta. El resultado. */
  routed: number;
  /** Pedidos que no quedaron en ruta, por cualquier causa. */
  dropped: number;
  headline: string;
  /** Lo que no entró, o `''` cuando entró todo. */
  droppedLabel: string;
}

/** `n` pedidos, con el singular cuando corresponde. */
function pedidos(n: number): string {
  return `${n} ${n === 1 ? 'pedido' : 'pedidos'}`;
}

/** Un descuento, o nada cuando es cero: un "0 sin flota" es ruido. */
function drop(count: number, label: string, target: DropTarget, hint?: string): FunnelDrop[] {
  return count > 0 ? [{ count, label, target, hint }] : [];
}

export function coverageFunnel(input: CoverageFunnelInput): CoverageFunnel {
  const loaded = Math.max(0, input.loaded ?? 0);
  const plannable = Math.max(0, input.plannable ?? 0);
  const unplanned = Math.max(0, input.unplanned ?? 0);
  const unassignedOrders = Math.max(0, input.unassignedOrders ?? 0);

  const reached =
    input.reached === undefined || input.reached === null
      ? Math.max(0, plannable - unplanned)
      : Math.max(0, input.reached);
  // Nunca negativo: un dato incoherente no puede pintar un resultado imposible.
  const routed = Math.max(0, reached - unassignedOrders);
  const dropped = Math.max(0, loaded - routed);

  // La equivalencia SOLO donde hubo agrupamiento de verdad. Si coinciden no hay nada
  // que declarar, y con MENOS pedidos que visitas el dato es inconsistente: afirmar
  // una fusión sería inventar. Misma regla que `groupedOrdersNote`.
  const visits = Math.max(0, input.reachedVisits ?? 0);
  const aside =
    visits > 0 && reached > visits
      ? `= ${visits} visitas · los pedidos de un mismo cliente se agrupan en una`
      : undefined;

  const levels: FunnelLevel[] = [
    {
      count: loaded,
      label: 'cargados',
      drops: [
        ...drop(
          Math.max(0, input.invalidGeo ?? 0),
          'sin coordenadas válidas',
          'excluded',
          'No se pueden rutear: revisar la geolocalización del cliente.',
        ),
        ...drop(
          Math.max(0, input.missingIdentity ?? 0),
          'sin cliente o sin número de pedido',
          'excluded',
          'Sin esos dos datos no se puede rutear: hay que completarlos en el pedido.',
        ),
      ],
    },
    {
      count: plannable,
      label: 'con datos completos',
      drops: drop(
        unplanned,
        'sin camión que los lleve',
        'fleet-gap',
        'Ningún camión asignado puede llevarlos. Cada grupo dice qué falta y en qué pantalla se corrige.',
      ),
    },
    {
      count: reached,
      label: 'llegaron al optimizador',
      aside,
      drops: drop(unassignedOrders, 'no cupieron en el camión de su ruta', 'unassigned'),
    },
  ];

  return {
    loaded,
    levels,
    routed,
    dropped,
    headline: `${routed} de ${pedidos(loaded)} en ruta`,
    droppedLabel: dropped > 0 ? `${pedidos(dropped)} no entraron` : '',
  };
}
